import api from 'services/api';
import { getConfigField, saveConfigurations } from 'lib/configUtils';

/* Motor de tradução - busca termos no indexedDB local e, para os que faltam, consulta a API */
class LangEngine {
  constructor(dbName = 'translationsDB') {
    this.dbName = dbName;
    this.language = this.getDefaultLanguage(); // define o idioma automaticamente
  }

  // método para obter o idioma padrão
  getDefaultLanguage() {
    return navigator.language || 'en'; // padrão para o idioma do navegador
  }

  // método para abrir a conexão com indexedDB
  async openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains('translations')) {
          db.createObjectStore('translations', {
            keyPath: 'term'
          });
        }
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        reject('Erro ao abrir o banco de dados: ' + event.target.errorCode);
      };
    });
  }

  // método para obter os termos do indexedDB
  async getStoredTerms(termsNeeded) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['translations'], 'readonly');
      const store = transaction.objectStore('translations');

      const termsFound = {};
      const missingTerms = [];

      let count = 0;

      termsNeeded.forEach((term) => {
        const request = store.get(term);

        request.onsuccess = (event) => {
          const result = event.target.result;

          if (result) {
            termsFound[term] = result.text;
          } else {
            missingTerms.push(term);
          }

          count++;

          if (count === termsNeeded.length) {
            resolve({
              termsFound,
              missingTerms
            });
          }
        };

        request.onerror = (event) => {
          reject('Erro ao buscar os termos: ' + event.target.errorCode);
        };
      });
    });
  };

  // método para armazenar os novos termos no indexedDB
  async storeTerms(terms) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['translations'], 'readwrite');
      const store = transaction.objectStore('translations');

      // transforma o objeto {key: value} em [{term: key, text: value}]
      Object.entries(terms).forEach(([key, value]) => {
        store.put({
          term: key,
          text: value
        });
      });

      transaction.oncomplete = () => {
        resolve('Termos armazenados com sucesso!');
      };

      transaction.onerror = (event) => {
        reject('Erro ao armazenar os termos: ' + event.target.errorCode);
      };
    });
  };

  // método para buscar os termos da API para os que faltam
  async fetchFromAPI(missingTerms, coddialeto) {
    try {
      const response = await api.post(`/traducao/${coddialeto}/findlist`, {
        listid: missingTerms
      });

      return response.data; // supondo que retorna um objeto { term: translation }
    } catch (error) {
      console.error('Erro ao buscar os termos:', error);
      return {};
    }
  };

  // método para buscar as configurações do sistema e salvá-las em cache local
  getConfiguracoes = async () => {
    try {
      const aURL = `/configuracoes/${1}`;
      const response = await api.get(aURL);

      saveConfigurations(response.data[0], 'cfg');

      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  // método principal para buscar e retornar todos os termos necessários
  async fetchTerms(termsNeeded) {
    const { termsFound, missingTerms } = await this.getStoredTerms(termsNeeded);

    if (missingTerms.length > 0) {
      console.log('Vai acionar a API...');

      let coddialeto = parseInt(getConfigField('coddialeto'), 10);

      if (isNaN(coddialeto)) {
        console.log('Vai carregar as configurações...')
        const response = await this.getConfiguracoes();
        saveConfigurations(response.data);

        console.log('Carregando o dialeto...');
        coddialeto = parseInt(await getConfigField('coddialeto'), 10);
      }

      const fetchedTerms = await this.fetchFromAPI(missingTerms, coddialeto);

      await this.storeTerms(fetchedTerms);

      return {
        ...termsFound,
        ...fetchedTerms
      };
    }

    return termsFound;
  }
};

export default new LangEngine();